import { parsePhoneNumber } from 'awesome-phonenumber';

import { EnumUserRelationshipsType, type User } from 'lib/graphql';

/**
 * Validates a phone number using awesome-phonenumber library
 * @param phoneNumber - Phone number string to validate
 * @param options - Optional configuration
 * @param options.allowEmpty - If true, returns true for null/undefined values (default: false)
 * @returns true if the phone number is valid, false otherwise
 */
export function isPhoneNumberValid(
  phoneNumber: string | null | undefined,
  options?: { allowEmpty?: boolean },
): boolean {
  if (!phoneNumber) {
    return options?.allowEmpty ?? false;
  }
  try {
    const phone = parsePhoneNumber(phoneNumber, { regionCode: 'US' });
    return phone.valid;
  } catch {
    return false;
  }
}

/**
 * Formats a phone number string for display
 * @param value - Raw phone number (digits only or with formatting), can be null/undefined
 * @returns Formatted phone number like (555) 123-4567, or '-' if no value provided
 */
export function formatPhoneDisplay(value: string | null | undefined): string {
  if (!value) return '-';

  try {
    // Parse the phone number using awesome-phonenumber to handle country codes
    const phone = parsePhoneNumber(value, { regionCode: 'US' });

    // If valid, format as national number (without country code)
    if (phone.valid) {
      // Get national number (e.g., "(555) 123-4567")
      return phone.number?.national || '-';
    }
  } catch {
    // Fall through to manual formatting if parsing fails
  }

  // Fallback: Manual formatting for invalid/unparseable numbers
  const digits = value.replace(/\D/g, '');

  // Remove leading '1' if present (US country code)
  const cleanDigits =
    digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;

  if (cleanDigits.length === 0) return '-';
  if (cleanDigits.length <= 3) return cleanDigits;
  if (cleanDigits.length <= 6)
    return `(${cleanDigits.slice(0, 3)}) ${cleanDigits.slice(3)}`;
  return `(${cleanDigits.slice(0, 3)}) ${cleanDigits.slice(3, 6)}-${cleanDigits.slice(6, 10)}`;
}

/**
 * Extracts only digits from a phone number string
 * @param value - Phone number with any formatting
 * @returns Digits only, max 10 characters
 */
export function extractPhoneDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 10);
}

export function formatUserRelationshipType(
  user: User | undefined | null,
  vendorId?: string,
): string {
  if (!user) return '-';
  const matches = user.relationships?.filter(
    (relationship) => relationship?.vendorId === vendorId,
  );
  const types = (matches ?? [])
    .map((m) => m?.type)
    .filter((t): t is EnumUserRelationshipsType => t != null);

  if (types.length === 1) {
    const t = types[0];
    return t === EnumUserRelationshipsType.Buyer ? 'Buyer' : 'Producer';
  }
  if (types.length > 1) {
    const hasBuyer = types.includes(EnumUserRelationshipsType.Buyer);
    const hasProducer = types.includes(EnumUserRelationshipsType.Producer);
    if (hasBuyer && hasProducer) return 'Buyer & Producer';
    if (hasBuyer) return 'Buyer';
    if (hasProducer) return 'Producer';
  }
  return '-';
}
